import type { ReactNode } from 'react';

import type { HqIconName } from './nav';

/**
 * The line icons of Dale HQ: 24 by 24, 1.7 stroke, round ends, in the ink of the
 * text around them. Drawn here so the panel carries no icon font and the same
 * glyph is the same in the sidebar, the tab bar and a button.
 */

const PATHS: Record<HqIconName, ReactNode> = {
  inicio: <path d="M4 10.6 12 4l8 6.6V19a1 1 0 0 1-1 1h-4v-5.5H9V20H5a1 1 0 0 1-1-1z" />,
  clientes: (
    <>
      <path d="M5 20V5.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1V20" />
      <path d="M15 9.5h3a1 1 0 0 1 1 1V20" />
      <path d="M3 20h18" />
      <path d="M8.5 8.5h3M8.5 12h3M8.5 15.5h3" />
    </>
  ),
  finanzas: (
    <>
      <path d="M4 4.5V19a1 1 0 0 0 1 1h15" />
      <path d="m7.5 14 3.5-4 3 3 4.5-6" />
    </>
  ),
  ia: (
    <>
      <path d="M10 5.5 11.4 9.6 15.5 11 11.4 12.4 10 16.5 8.6 12.4 4.5 11 8.6 9.6z" />
      <path d="M18 3.5v4M16 5.5h4" />
      <path d="M18 14.5v4M16 16.5h4" />
    </>
  ),
  equipo: (
    <>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3 3 0 0 1 0 6" />
      <path d="M17.5 14.5a5.5 5.5 0 0 1 3.5 5" />
    </>
  ),
  organizaciones: (
    <>
      <path d="m12 3.5 8.5 4.7L12 13 3.5 8.2z" />
      <path d="m3.5 12.5 8.5 4.8 8.5-4.8" />
    </>
  ),
  pagos: (
    <>
      <path d="M5.5 5.5a1.5 1.5 0 0 1 1.5-1.5h10a1.5 1.5 0 0 1 1.5 1.5V20l-2.4-1.6-2.3 1.6-2.3-1.6-2.3 1.6-2.4-1.6-.7.5z" />
      <path d="M9 9h6M9 12.5h4" />
    </>
  ),
  mas: (
    <>
      <circle cx="5.5" cy="12" r="1.2" />
      <circle cx="12" cy="12" r="1.2" />
      <circle cx="18.5" cy="12" r="1.2" />
    </>
  ),
  buscar: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  luna: <path d="M20 14.6A8 8 0 0 1 9.4 4 8 8 0 1 0 20 14.6z" />,
  sol: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7" />
    </>
  ),
  derecha: <path d="M5 12h14M13 6l6 6-6 6" />,
  izquierda: <path d="M19 12H5M11 6l-6 6 6 6" />,
  abajo: <path d="m6 9 6 6 6-6" />,
  agregar: <path d="M12 5v14M5 12h14" />,
  cerrar: <path d="m6 6 12 12M18 6 6 18" />,
  enviar: <path d="M12 19V5M6 11l6-6 6 6" />,
  salir: (
    <>
      <path d="M10 4.5H6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h4" />
      <path d="m15 8 4 4-4 4M19 12H9.5" />
    </>
  ),
};

type HqIconProps = { name: HqIconName; size?: number };

export function HqIcon({ name, size = 22 }: HqIconProps) {
  return (
    <svg
      className="hq-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
